"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, ChevronRight, Video, Loader2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  uploadVideoKYCRequest,
  updateKycFieldsRequest,
} from "@/store/slices/kyc.slice";
import { formDataUtils } from "@/services/kycService";
import { getUpdatableFieldsFromProblems } from "@/types/models/kyc";
import { store } from "@/store/store";
import FileUpload from "./FileUpload";
import { whitelabelConfig } from "@/config/whitelabel";

// Validation Schema
const videoVerificationSchema = z.object({
  video_kyc_file: z
    .any()
    .optional()
    .refine(
      (file) => !file || file instanceof File,
      "Video KYC file must be a valid file",
    ),
});

type VideoVerificationFormData = {
  video_kyc_file?: File;
};

interface VideoVerificationStepProps {
  onPrevious: () => void;
  storeId: string;
  kycData?: any | null;
}

export const VideoVerificationStep: React.FC<VideoVerificationStepProps> = ({
  onPrevious,
  storeId,
  kycData: _kycData = null,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading, error, isResubmissionMode, rejectedSteps, kycProblems } =
    useAppSelector((state) => state.kyc);

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<VideoVerificationFormData>({
    resolver: zodResolver(videoVerificationSchema),
  });

  // Update form values when file changes
  React.useEffect(() => {
    if (videoFile) {
      setValue("video_kyc_file", videoFile);
    }
  }, [videoFile, setValue]);

  const onSubmit = async () => {
    setSubmitError(null);

    if (!videoFile) {
      setSubmitError("Please upload video KYC file");
      return;
    }

    try {
      // Use PATCH endpoint for resubmissions, POST for initial submissions
      if (isResubmissionMode && rejectedSteps?.videoVerification) {
        // PATCH: Update only the fields that were actually rejected
        const updatableFields = getUpdatableFieldsFromProblems(
          kycProblems || [],
        );
        const fields: Record<string, unknown> = {};

        // Only include video file if it was marked as problematic
        if (updatableFields.includes("video_kyc_file") && videoFile) {
          fields.video_kyc_file = videoFile;
        }

        // Ensure we have at least one field to update
        if (Object.keys(fields).length === 0) {
          setSubmitError(
            "No problematic video found to update. Please contact support.",
          );
          return;
        }

        await new Promise((resolve, reject) => {
          const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (!state.kyc.isLoading) {
              unsubscribe();
              if (state.kyc.error) {
                reject(new Error(state.kyc.error));
              } else {
                resolve(true);
              }
            }
          });

          dispatch(
            updateKycFieldsRequest({
              storeId,
              fields,
            }),
          );
        });
      } else {
        // POST: Initial video upload
        const formData = formDataUtils.createVideoVerificationFormData({
          video_kyc_file: videoFile,
        });

        await new Promise((resolve, reject) => {
          const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (!state.kyc.isLoading) {
              unsubscribe();
              if (state.kyc.error) {
                reject(new Error(state.kyc.error));
              } else if (state.kyc.completedSteps.videoVerification) {
                resolve(true);
              }
            }
          });

          dispatch(
            uploadVideoKYCRequest({
              storeId,
              formData,
            }),
          );
        });
      }

      // Success - Redux will automatically advance to the next step
      console.log(
        "[VideoVerificationStep] Video uploaded successfully, Redux will handle step advancement",
      );
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to upload video KYC. Please try again.",
      );
    }
  };

  const videoInstructions = [
    "Record a short video (30-60 seconds) of yourself",
    "Hold your PAN card next to your face",
    "Clearly state your full name and business name",
    "Ensure good lighting and clear audio",
    "Keep the video steady and in focus",
  ];

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          Video Verification
        </h2>
        <p className="text-auth-text-muted">
          Record a short verification video to complete your KYC process. This
          helps us verify your identity.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Video Upload */}
        <FileUpload
          onFileSelect={setVideoFile}
          accept="video/mp4,video/webm,video/quicktime"
          maxSize={50 * 1024 * 1024} // 50MB
          fileType="video"
          label="Video KYC"
          description="Upload your verification video (MP4, WebM, or QuickTime, max 50MB)"
          required
          currentFile={videoFile}
          error={errors.video_kyc_file?.message}
          isUploading={isLoading}
        />

        {/* Video Preview */}
        {videoFile && (
          <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
            <h3 className="text-white font-medium mb-3">Video Preview:</h3>
            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                src={URL.createObjectURL(videoFile)}
                controls
                className="w-full max-h-64 object-contain"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
          <h3 className="text-white font-medium mb-3 flex items-center space-x-2">
            <Video className="w-5 h-5 text-brand" />
            <span>Video Recording Instructions:</span>
          </h3>
          <div className="space-y-2">
            {videoInstructions.map((instruction, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-brand rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-medium">
                    {index + 1}
                  </span>
                </div>
                <span className="text-auth-text-muted text-sm">
                  {instruction}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sample Script */}
        <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
          <h3 className="text-white font-medium mb-2">Sample Script:</h3>
          <div className="bg-auth-surface-dark rounded-lg p-3">
            <p className="text-auth-text text-sm italic">
              &quot;Hello, my name is [Your Full Name]. I am the owner of
              [Business Name]. I am completing my KYC verification for
              {whitelabelConfig.brandName}. This is my PAN card [show PAN card clearly]. Thank
              you.&quot;
            </p>
          </div>
        </div>

        {/* Technical Requirements */}
        <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
          <h3 className="text-white font-medium mb-2">
            Technical Requirements:
          </h3>
          <ul className="text-auth-text-muted text-sm space-y-1">
            <li>• Video duration: 30-60 seconds</li>
            <li>• File formats: MP4, WebM, or QuickTime</li>
            <li>• Maximum file size: 50MB</li>
            <li>• Resolution: Minimum 720p recommended</li>
            <li>• Ensure clear audio and video quality</li>
            <li>• Record in landscape orientation if possible</li>
          </ul>
        </div>

        {/* Error Message */}
        {(error || submitError) && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-500 text-sm">{error || submitError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="flex items-center space-x-2 px-6 py-3 bg-muted/50 border border-border rounded-[30px] text-muted-foreground hover:text-foreground hover:bg-muted hover:border-muted-foreground/50 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ChevronDown className="w-5 h-5 rotate-90" />
            <span>Previous</span>
          </button>

          <button
            type="submit"
            disabled={isLoading || !videoFile}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:bg-primary/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl rounded-[30px] transform hover:scale-105 active:scale-95"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isLoading ? "Uploading..." : "Submit KYC"}</span>
            {!isLoading && <ChevronRight className="w-5 h-5" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VideoVerificationStep;
